import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Search, ExternalLink, Megaphone, Instagram, Youtube, Twitter, MapPin } from "lucide-react";

const CATEGORIES = [
  "Fashion", "Beauty", "Tech", "Food", "Travel",
  "Fitness", "Gaming", "Business", "Lifestyle", "Education",
];

function fmtFollowers(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}

const PLATFORM_ICON = {
  instagram: Instagram,
  youtube: Youtube,
  twitter: Twitter,
  tiktok: Megaphone,
  facebook: ExternalLink,
};

export default function Influencers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (category) params.category = category;
    const { data } = await api.get("/influencers", { params });
    setItems(data);
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, category]);

  return (
    <div className="space-y-6" data-testid="influencers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tag"><span className="w-1.5 h-1.5 bg-pink-500" /> Marketplace</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3">Influencer marketplace</h1>
          <p className="text-zinc-600 text-sm mt-1">
            Discover creators by category, audience and platform. Recruit for campaigns.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setCategory("")}
          data-testid="influencer-cat-all"
          className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${
            !category ? "bg-[#09090b] text-white border-[#09090b]" : "border-zinc-300 hover:bg-zinc-100"
          }`}
        >All</button>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`influencer-cat-${c.toLowerCase()}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-semibold border ${
              category === c ? "bg-[#09090b] text-white border-[#09090b]" : "border-zinc-300 hover:bg-zinc-100"
            }`}
          >{c}</button>
        ))}
      </div>

      <div className="border border-zinc-200 bg-white">
        <div className="flex items-center gap-2 p-3 border-b border-zinc-200">
          <Search className="w-4 h-4 text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="influencer-search"
            placeholder="Search influencers by name…"
            className="flex-1 outline-none text-sm bg-transparent"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" data-testid="influencer-grid">
          {items.length === 0 && (
            <div className="p-10 text-center text-zinc-500 col-span-full text-sm">No influencers yet. As Influencer members complete their profile, they'll appear here.</div>
          )}
          {items.map((i) => (
            <div key={i.user_id} className="border-b border-r border-zinc-200 p-5" data-testid={`influencer-card-${i.user_id}`}>
              <div className="flex items-center gap-3">
                {i.picture ? (
                  <img src={i.picture} alt="" className="w-12 h-12 object-cover" />
                ) : (
                  <div className="w-12 h-12 bg-zinc-100 grid place-items-center text-zinc-500">
                    <Megaphone className="w-5 h-5" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-display font-black text-base tracking-tight truncate flex items-center gap-1">
                    {i.name || "Unnamed Creator"}
                    {i.is_verified && (
                      <span title="Verified Influencer" data-testid={`influencer-verified-${i.user_id}`} className="text-emerald-600">
                        ✓
                      </span>
                    )}
                  </div>
                  {i.location && (
                    <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" /> {i.location}
                    </div>
                  )}
                </div>
              </div>

              {i.bio && <p className="text-sm text-zinc-600 mt-3 line-clamp-2">{i.bio}</p>}

              {i.content_categories?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {i.content_categories.slice(0, 4).map((c) => (
                    <span key={c} className="text-[10px] uppercase tracking-widest font-semibold border border-zinc-200 px-1.5 py-0.5">{c}</span>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                  {Object.entries(i.social_links || {}).slice(0, 4).map(([k, v]) => {
                    const Icon = PLATFORM_ICON[k] || ExternalLink;
                    if (!v) return null;
                    return (
                      <a key={k} href={v} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-[#C8102E]" data-testid={`influencer-social-${k}-${i.user_id}`}>
                        <Icon className="w-4 h-4" />
                      </a>
                    );
                  })}
                </div>
                <div className="font-mono text-zinc-600">
                  {Object.entries(i.follower_counts || {})
                    .sort((a, b) => Number(b[1]) - Number(a[1]))
                    .slice(0, 1)
                    .map(([k, v]) => (
                      <span key={k}>{fmtFollowers(v)} on {k}</span>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
